import { cn } from '@immo/ui-kit';
import { SEARCH_GROUP_LIMIT, type SearchEntityType, type SearchResultGroup } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { ContactsIcon, FileIcon, InboxIcon, PropertiesIcon, TableIcon } from '@/components/layout/icons';
import { Kbd } from './kbd';
import type { PaletteItem } from './palette-item';

const GROUP_ICON = {
  kontakt: ContactsIcon,
  objekt: PropertiesIcon,
  deal: TableIcon,
  lead: InboxIcon,
  dokument: FileIcon,
} as const satisfies Record<SearchEntityType, unknown>;

type Props = {
  readonly listboxId: string;
  readonly groups: ReadonlyArray<SearchResultGroup>;
  readonly items: ReadonlyArray<PaletteItem>;
  readonly activeIndex: number;
  readonly onHighlight: (index: number) => void;
  readonly onSelect: (item: PaletteItem) => void;
};

/**
 * The grouped result list. Groups arrive in the contract's fixed order and are
 * rendered in it; a group the member may not read never arrives at all, and a
 * group that was searched and matched nothing renders as searched-and-empty.
 *
 * The flat `items` list is what the keyboard walks, so the highlight crosses
 * group boundaries without this component knowing anything about it.
 */
export function SearchResults({ listboxId, groups, items, activeIndex, onHighlight, onSelect }: Props) {
  const t = useTranslations('pages.shell.search');
  const tGroup = useTranslations('pages.shell.search.groups');
  const tStatus = useTranslations('pages.shell.search.status');

  const secondaryLine = (item: PaletteItem) => {
    const parts = [
      item.secondary,
      item.secondaryToken === null ? null : tStatus(`${item.groupKey}.${item.secondaryToken}`),
    ];

    return parts.filter((part) => part !== null && part !== '').join(' · ');
  };

  return (
    <ul id={listboxId} role="listbox" aria-label={t('results-label')} className="max-h-80 overflow-y-auto p-2">
      {groups.map((group) => {
        const Icon = GROUP_ICON[group.entityType];
        const groupItems = items.filter((item) => item.groupKey === group.entityType);
        const truncated = group.total > SEARCH_GROUP_LIMIT;
        // „{shown} von {total}" belongs to the „Alle Treffer" row when the group
        // has one (the design run puts the count there); a truncated group
        // WITHOUT that row — Dokumente, which has no list to open — keeps it in
        // the heading so its count is never lost.
        const countOnRow = groupItems.some((item) => item.kind === 'all-results');
        const count = t('truncated', { shown: group.rows.length, total: group.total });

        return (
          <li key={group.entityType} role="presentation">
            <div className="flex items-baseline justify-between gap-3 px-2 py-1.5">
              <p className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
                {tGroup(group.entityType)}
              </p>
              {truncated && !countOnRow && <p className="shrink-0 text-xs text-text-tertiary">{count}</p>}
            </div>
            {group.rows.length === 0 ? (
              <p className="px-2 py-2 text-sm text-text-tertiary">{t('group-empty')}</p>
            ) : (
              <ul role="presentation" className="flex flex-col">
                {groupItems.map((item) => {
                  const index = items.indexOf(item);
                  const secondary = secondaryLine(item);

                  return (
                    <li
                      key={item.key}
                      id={item.optionId}
                      role="option"
                      aria-selected={index === activeIndex}
                      onMouseEnter={() => onHighlight(index)}
                      onClick={() => onSelect(item)}
                      className={cn(
                        'flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-left transition-colors',
                        index === activeIndex ? 'bg-selected-bg' : 'hover:bg-hover-bg',
                      )}
                    >
                      {item.kind === 'all-results' ? (
                        // The „Mehr"-signal: same 24-px chip tile as a result
                        // row, dashed and glyph-less, so the row reads as the
                        // group's continuation rather than another record.
                        <span className="h-6 w-6 shrink-0 rounded-md border border-dashed border-border" aria-hidden />
                      ) : (
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-neutral-100">
                          <Icon className="h-3.5 w-3.5 text-text-secondary" aria-hidden />
                        </span>
                      )}
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-sm text-text-primary">{item.primary}</span>
                        {secondary !== '' && <span className="truncate text-xs text-text-tertiary">{secondary}</span>}
                      </span>
                      {item.kind === 'all-results' && (
                        <span className="shrink-0 text-xs text-text-tertiary">{count}</span>
                      )}
                      {index === activeIndex && <Kbd>{'↵'}</Kbd>}
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}
